import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { cn } from '@localagent/ui/lib/utils';
import { Popover } from '@localagent/ui/components/popover';
import { authIdentityAtom } from '../../stores/auth/atoms';
import { unreadErrorCountAtom } from '../../stores/errors/atoms';
import { sdkConfiguredAtom } from '../../stores/sdk/atoms';
import { getSDK } from '../../services/sdk-instance';
import { useSignOut } from '../../hooks/useSignOut';

export function emailInitials(email: string): string {
  const at = email.indexOf('@');
  if (at <= 0) {
    throw new Error(`login email has no local-part: ${email}`);
  }
  const local = email.slice(0, at);
  const letters = local.replace(/[^a-zA-Z0-9]/g, '');
  if (letters.length === 0) {
    throw new Error(`login email local-part has no initials source: ${email}`);
  }
  return letters.slice(0, 2).toUpperCase();
}

export interface UserNavFooterMenuProps {
  email: string;
  onSignOut: () => void;
  isSigningOut: boolean;
  compact?: boolean;
  unseenCount?: number | null;
  unseenError?: string | null;
}

export function UserNavFooterMenu({
  email,
  onSignOut,
  isSigningOut,
  compact = false,
  unseenCount,
  unseenError,
}: UserNavFooterMenuProps) {
  const [open, setOpen] = useState(false);
  const unreadErrors = useAtomValue(unreadErrorCountAtom);
  const initials = emailInitials(email);

  return (
    <div className="shrink-0 p-2" data-testid="user-nav-footer">
      <Popover
        open={open}
        onOpenChange={setOpen}
        align="start"
        aria-label="Account"
        className="block w-full"
        triggerClassName={cn(
          'w-full rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
          compact ? 'justify-center px-0 py-1.5' : 'justify-start px-2 py-1.5',
        )}
        contentClassName={cn('bottom-full mb-1 mt-0', compact ? 'min-w-48' : 'w-full')}
        trigger={
          <span
            data-testid="account-menu"
            data-compact={compact ? 'true' : 'false'}
            className="flex w-full min-w-0 items-center gap-2"
          >
            <span
              aria-hidden
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-micro font-semibold text-primary-foreground"
            >
              {initials}
            </span>
            {compact ? null : (
              <>
                <span className="min-w-0 flex-1 truncate text-left text-label font-medium">{email}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </>
            )}
          </span>
        }
      >
        <div className="flex flex-col gap-0.5">
          <NavLink
            to="/support"
            data-testid="footer-support"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              cn(
                'rounded-sm px-2 py-1.5 text-body transition-colors hover:bg-accent',
                isActive && 'bg-accent',
              )
            }
          >
            Support
            {unseenError
              ? ''
              : unseenCount != null && unseenCount > 0
                ? ` (${unseenCount})`
                : ''}
            {unseenError ? (
              <span className="ml-1 text-destructive" data-testid="footer-support-unseen-error">
                {unseenError}
              </span>
            ) : null}
          </NavLink>
          <NavLink
            to="/settings?section=errors"
            data-testid="footer-unread-errors"
            onClick={() => setOpen(false)}
            className="rounded-sm px-2 py-1.5 text-body transition-colors hover:bg-accent"
          >
            Errors{unreadErrors > 0 ? ` (${unreadErrors})` : ''}
          </NavLink>
          <NavLink
            to="/secrets"
            data-testid="nav-secrets"
            onClick={() => setOpen(false)}
            className="rounded-sm px-2 py-1.5 text-body transition-colors hover:bg-accent"
          >
            Secrets
          </NavLink>
          <NavLink
            to="/settings"
            data-testid="nav-settings"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              cn(
                'rounded-sm px-2 py-1.5 text-body transition-colors hover:bg-accent',
                isActive && 'bg-accent',
              )
            }
          >
            Settings
          </NavLink>
          <NavLink
            to="/docs"
            data-testid="nav-docs"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              cn(
                'rounded-sm px-2 py-1.5 text-body transition-colors hover:bg-accent',
                isActive && 'bg-accent',
              )
            }
          >
            Docs
          </NavLink>
          <button
            type="button"
            data-testid="logout-button"
            onClick={onSignOut}
            disabled={isSigningOut}
            className="w-full rounded-sm px-2 py-1.5 text-left text-body transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSigningOut ? 'Signing out…' : 'Sign Out'}
          </button>
        </div>
      </Popover>
    </div>
  );
}

export function UserNavFooter({ compact = false }: { compact?: boolean }) {
  const identity = useAtomValue(authIdentityAtom);
  const configured = useAtomValue(sdkConfiguredAtom);
  const { signOut, isSigningOut } = useSignOut();
  const unseen = useQuery({
    queryKey: ['support', 'unseen'],
    queryFn: () => getSDK().support.unseen(),
    enabled: identity !== null && configured,
    refetchOnWindowFocus: true,
  });

  if (!identity) return null;

  return (
    <UserNavFooterMenu
      email={identity.email}
      onSignOut={() => {
        void signOut();
      }}
      isSigningOut={isSigningOut}
      compact={compact}
      unseenCount={unseen.data?.count}
      unseenError={
        unseen.isError
          ? unseen.error instanceof Error
            ? unseen.error.message
            : String(unseen.error)
          : null
      }
    />
  );
}
